import {
  accountTypes,
  cleanContributoryInvestments,
  fundingTypes,
  getAppSourceType,
  setDefaultedProfileData,
  secondaryCommitMessage,
  applicationStatusTypes
} from '../shared/utils';
import {
  pollForSubmissionResponse,
  commitAdviceConsent,
  commitConsent,
  loadTermsForSubmission,
  loadUser,
  initiateAuthService,
  getUserExistingAgreements
} from '../shared/loaders';
import {
  mergeTyped,
  parseAmount,
  isEmpty,
  removeCommasAndParseFloat,
  withDefault
} from '../../core/util';
import {
  ApplicationState,
  PartialStateStore,
  StateStore,
  StateStoreOrVoid
} from '../../core/types/application-state';
import { DataLoader } from '../../core/types/data-loader';
import {
  analyticsDataLayer,
  dispatchPageLoad
} from '../../analytics/analytics-helper';
import {
  detailedFailureInfo,
  getPLAIDFailures,
  getPLAIDAuthenticationFailures,
  plaidResultDefaults
} from '../shared/plaid-utils';
import {
  BankAccountReference,
  BankFailure
} from '../../core/types/bank-acount';
import {
  validationFailure,
  onSuccess as onPlaidSuccess
} from '../shared/services/external-financial-institution-service';

import {
  accountTypesAnalytics,
  productTypesAnalytics
} from '../../analytics/util';

import {
  getCurrentYearContribLimits,
  getCustomerContribLimits as getCustomerContributoryLimitsService,
  getPriorYearContribLimits
} from '../shared/services/ira-contribution-limits';
import { retrieveFilterAndAssignAppID } from '../shared/services/profile-service';
import { getContributionPeriod as getContributionPeriodService } from '../shared/services/contribution-period-service';
import { createContributoryApplicationService } from '../shared/services/mutualfund-contributory';
import { createNewReturnJourneyProfile } from '../return-journey/loaders';
import { encode_deviceprint as encodeDeviceprint } from '../../rsa-fingerprint';
import { convertProspectCustomer } from '../shared/services/customer-service';

export const getContributionPeriod = async (
  userResults: any
): Promise<any[]> => {
  // default to Hawaii since it is the latest US timezone
  const residentialState =
    userResults?.app?.customer?.residentialAddress?.state || 'HI';

  const contributionPeriodResults = await getContributionPeriodService(
    residentialState
  );

  return contributionPeriodResults;
};

export const getNewUserContributionLimits = async (): Promise<any> => {
  // prior year higher limit is based off age 51, as age 50 in the prior year would be 49
  // ages 51+ will give the higher limit of 7K, while ages <= 50 will give limit of 6K
  const newUserLowerLimitPriorYear = await getPriorYearContribLimits(50);
  const newUserHigherLimitPriorYear = await getPriorYearContribLimits(51);
  const newUserLowerLimitCurrentYear = await getCurrentYearContribLimits(49);
  const newUserHigherLimitCurrentYear = await getCurrentYearContribLimits(50);
  return {
    newUserLowerLimitPriorYear:
      newUserLowerLimitPriorYear[0]?.contributionLimitAmount,
    newUserHigherLimitPriorYear:
      newUserHigherLimitPriorYear[0]?.contributionLimitAmount,
    newUserLowerLimitCurrentYear:
      newUserLowerLimitCurrentYear[0]?.contributionLimitAmount,
    newUserHigherLimitCurrentYear:
      newUserHigherLimitCurrentYear[0]?.contributionLimitAmount
  };
};

export const loadLoginData: DataLoader = async (
  _,
  flowProps
): Promise<StateStoreOrVoid> => {
  const authDetails = await initiateAuthService();

  const queryParams = window.location.search;
  return {
    authenticated: authDetails?.authenticated,
    accountType: accountTypes.CONTRIBUTORY,
    ...(!isEmpty(queryParams) && {
      routeData: {
        queryString: queryParams
      }
    }),
    app: {
      iraAccountType: flowProps?.iraAccountType
    }
  };
};

export const makeLoadProfileData = (iraAccountType): DataLoader =>
  async function loadProfileData(
    a: ApplicationState
  ): Promise<StateStoreOrVoid> {
    let existingProfileAppID = '';
    const userResults = await loadUser(a);

    const contributionPeriod = await getContributionPeriod(userResults);

    const contributionLimits =
      userResults && userResults?.retailEnabled
        ? await getCustomerContributoryLimitsService(
            userResults?.app?.customer?.dob
          )
        : await getNewUserContributionLimits();

    // use IRA type commited to app if applicable
    const {
      store: {
        app: { iraAccountType: appIraAccountType }
      }
    } = a;
    const iraType = withDefault(appIraAccountType, iraAccountType);

    const existingAgreementsResults = await getUserExistingAgreements(
      userResults,
      [accountTypes[`${iraType}`]]
    );
    if (
      userResults?.app?.customer?.primaryEmailAddress &&
      userResults?.authenticated
    ) {
      existingProfileAppID = await retrieveFilterAndAssignAppID(
        userResults?.app?.customer?.primaryEmailAddress,
        'CONTRIBUTORY_IRA'
      );
    }

    return {
      ...mergeTyped<StateStoreOrVoid>(userResults, {
        app: {
          iraAccountType: iraType,
          profileApplicationID: existingProfileAppID
        },
        existingAgreements: existingAgreementsResults,
        accountType: accountTypes.CONTRIBUTORY,
        loadedData: {
          contributionLimits,
          contributionPeriod
        }
      })
    };
  };

export const commitProfileData: DataLoader = async ({
  chosen,
  inputs,
  store
}): Promise<StateStoreOrVoid> => {
  if (chosen === 'skipLogin') {
    return {};
  }

  const shoppingFunds = chosen;
  const { priorYearAmount = 0, currentYearAmount = 0 } = inputs;
  const { accountType, app } = store;
  // account for defaulted input values not persisting for customers with existing data
  const defaultedProfileData = setDefaultedProfileData(inputs, store);

  // fallback to fetch amount values from store, incase of modal action
  // wiping out input values
  const defaultPriorYearAmount = withDefault(
    priorYearAmount,
    store?.priorYearAmount
  );
  const defaultCurrentYearAmount = withDefault(
    currentYearAmount,
    store?.currentYearAmount
  );

  const existingProfileAppID = await retrieveFilterAndAssignAppID(
    defaultedProfileData.primaryEmailAddress,
    accountType
  );

  const storeToCommit = {
    accountType,
    amount:
      removeCommasAndParseFloat(defaultPriorYearAmount) +
      removeCommasAndParseFloat(defaultCurrentYearAmount),
    priorYearAmount: removeCommasAndParseFloat(defaultPriorYearAmount),
    currentYearAmount: removeCommasAndParseFloat(defaultCurrentYearAmount),
    app: {
      customer: {
        name: {
          firstName: defaultedProfileData.firstName,
          lastName: defaultedProfileData.lastName
        },
        primaryEmailAddress: defaultedProfileData.primaryEmailAddress,
        dob: defaultedProfileData.dob
      },
      iraAccountType: app?.iraAccountType
    },
    ...(shoppingFunds && { loadedData: { shoppingFunds } })
  };

  if (!existingProfileAppID)
    return createNewReturnJourneyProfile(storeToCommit);

  return {
    ...storeToCommit,
    app: { ...storeToCommit.app, profileApplicationID: existingProfileAppID }
  };
};

export const commitNewInvestmentsBYOP: DataLoader = ({ store, chosen }) => {
  const newInvestments = chosen.map(
    ({
      ticker,
      trpCode,
      amount: fundingAmount,
      percent: fundingAmountPercentage
    }) => ({
      ticker,
      trpCode,
      fundingDetails: {
        fundingAmount,
        fundingAmountPercentage
      }
    })
  );

  const totalAmount =
    chosen?.length > 0
      ? parseAmount(
          chosen
            .reduce((accumulator, { amount }) => {
              return accumulator + parseFloat(amount);
            }, 0)
            .toFixed(2)
        )
      : store?.amount;

  analyticsDataLayer({
    quantity: newInvestments.length,
    newInvestments
  });

  return {
    app: {
      newInvestments,
      investmentMethod: 'BYOP',
      existingAccounts: []
    },
    estimatedRolloverAmount: totalAmount,
    amount: totalAmount,
    chosenInvestments: chosen
  };
};

export const commitNewInvestmentsRDF: DataLoader = async ({
  store: {
    amount,
    app: { customer },
    termsData
  },
  chosen
}) => {
  // Bypasses commiting to store when not needed
  if (typeof chosen === 'string') return {};

  const { investmentMethod, fund } = chosen;
  const { ticker, trpCode } = fund;
  const dob = customer?.dob;

  const newInvestments = [
    {
      ticker,
      trpCode,
      fundingDetails: {
        fundingAmount: amount || 0,
        fundingAmountPercentage: 100
      }
    }
  ];

  analyticsDataLayer({
    quantity: newInvestments.length,
    newInvestments
  });

  const { consentID } = termsData;
  const rdfCommitResults = {
    amount,
    app: {
      newInvestments,
      investmentMethod,
      existingAccounts: []
    },
    estimatedRolloverAmount: amount,
    loadedData: { rdfData: { ...fund } },
    chosenInvestments: [{ ...fund, amount, percent: 100 }]
  };

  if (consentID && fund.cusip) {
    await commitAdviceConsent(consentID, { dob, amount, loadedData: fund });
  }
  return rdfCommitResults;
};

export const commitEverifyReference: DataLoader = ({
  store,
  chosen,
  result
}): StateStore => {
  const { app, existingAccounts, loadedData } = store;

  // for bank link failure thru everify, remove bank info reference and add bank failure information
  // bankInformationRefence set to undefined; purged for submission with purgeEmptyFields
  // failure scenario for evy will send evy fail status as result param for next(); details failure
  if (chosen === 'evy-failure') {
    const { bankFailureInformation = [] } = app;
    return {
      app: {
        bankInformationReference: undefined,
        bankFailureInformation: [
          ...bankFailureInformation,
          {
            bankChoiceMethod: fundingTypes.MANUAL,
            bankName: withDefault(existingAccounts?.bankName, ''),
            bankFailureReason: withDefault(result, 'everify failure')
          }
        ]
      }
    };
  }

  // for successfully linking bank thru everify, add manualEntryToken to existing bank information reference
  const { bankInformationReference } = app;

  return {
    app: {
      bankInformationReference: {
        ...bankInformationReference,
        manualEntryToken: loadedData?.evyToken
      }
    }
  };
};

export const loadContributoryLimits: DataLoader = async ({ store }) => {
  const { app, retailEnabled } = store;
  const { customer } = app;

  // if an existing retail customer then check their current contributions
  if (retailEnabled) {
    const contributionLimits = await getCustomerContributoryLimitsService(
      customer?.dob
    );

    return {
      loadedData: {
        contributionLimits
      }
    };
  }

  return {};
};

// @Abhinav - fix complexity
/* eslint-disable complexity */
export const commitBankAccount: DataLoader = async (
  appState
): Promise<PartialStateStore> => {
  const { chosen, result: onSuccessPayload, store } = appState;
  const { bankFailureInformation = [] } = store.app;
  const updatedFailureInformation = bankFailureInformation;

  if (chosen === 'on-success' && onSuccessPayload) {
    const {
      institution,
      account: { id: refId }
    } = onSuccessPayload.metadata;
    const { authenticationId } = onSuccessPayload;

    const accountsFromEFI = await onPlaidSuccess(onSuccessPayload);
    const relevantAccount = accountsFromEFI.find(
      acc => acc.referenceId === refId
    );
    const { bankName, ownerNames } = relevantAccount;
    const failures = getPLAIDFailures(appState, relevantAccount);
    const failureInfo = detailedFailureInfo(failures, institution);
    if (failures) {
      // checks if invesment amt exceeds the bank account balance
      if (failures?.validationBalanceFail) {
        /**
         * in the event of a validation failure
         * log to the efi validation-failure  endpoint
         * this is useful for analysis on what's holding
         * back our users.
         *
         * @ref: US239281
         */
        validationFailure({ ...failures, bankName });
        return {
          app: {
            bankInformationReference: undefined
          },
          loadedData: {
            plaidResult: {
              ...plaidResultDefaults,
              success: false,
              amountTooLow: true
            }
          }
        };
      }

      /**
       * in the event of a validation failure
       * log to the efi validation-failure  endpoint
       *
       * @ref: US239281
       */
      validationFailure({ ...failures, bankName });
      const bankFailure = BankFailure.check({
        bankName,
        bankChoiceMethod: fundingTypes.EXTERNAL,
        bankFailureReason: failureInfo?.failureInfo?.bankFailureReason
      });
      updatedFailureInformation.push(bankFailure);

      return {
        plaidFailures: failures,
        app: {
          bankInformationReference: undefined,
          bankFailureInformation: updatedFailureInformation
        },
        loadedData: {
          plaidResult: {
            ...plaidResultDefaults,
            success: false,
            ...failureInfo
          }
        }
      };
    }

    const { name: custodianName, institution_id: csdId } = institution;
    const externalAccounts = [
      {
        custodianName,
        authenticationId,
        ...relevantAccount
      }
    ];
    const custodians = [
      {
        custodianName,
        name: custodianName,
        id: csdId,
        csdId
      }
    ];

    const [externalAccount] = externalAccounts;
    const bankInformationReference = BankAccountReference.check({
      bankAccountType: externalAccount.accountType,
      bankChoiceMethod: fundingTypes.EXTERNAL,
      bankAccountOwners: ownerNames,
      externalProviderToken: externalAccount.referenceId,
      externalProviderAuthenticationToken: externalAccount.authenticationId
    });

    return {
      app: {
        bankInformationReference
      },
      loadedData: {
        plaidResult: {
          custodians,
          ...plaidResultDefaults,
          externalAccount,
          success: true
        }
      }
    };
  }
  // check if the PLAID linking failed due to invalid creds or MFA
  if (
    appState?.result?.errorCode === 'INVALID_CREDENTIALS' ||
    appState?.result?.errorCode === 'INVALID_MFA'
  ) {
    const authenticationFailures = getPLAIDAuthenticationFailures(
      appState?.result?.errorCode
    );
    const failedBankName = appState?.result?.institution?.name;

    /**
     * in the event of a validation failure
     * log to the efi validation-failure  endpoint
     *
     * @ref: US239281
     */
    validationFailure({ ...authenticationFailures, bankName: failedBankName });
    return {
      app: {
        bankInformationReference: undefined
      },
      loadedData: {
        plaidResult: {
          ...plaidResultDefaults,
          success: false
        }
      }
    };
  }
  return {
    app: {
      bankInformationReference: undefined
    },
    loadedData: {
      plaidResult: {
        ...plaidResultDefaults,
        success: false
      }
    }
  };
};
/* eslint-enable complexity */

export const createApplication: DataLoader = async ({
  store,
  chosen
}): Promise<StateStoreOrVoid> => {
  if (['byop', 'rdf', 'funding', 'info', 'contact'].includes(chosen)) {
    return { inReviewMode: true };
  }

  const { app, consumerName } = store;

  const { customer, termsIDs, newInvestments } = app;

  // newInvestments for contributory are formatted differently
  const cleanedInvestments = cleanContributoryInvestments(newInvestments);

  // use terms ids to create consent
  await commitConsent(termsIDs, customer);
  // terms ids should be an array of strings for service
  const stringTermsIDs = termsIDs?.map(id => id?.toString());
  // add rollover type and term IDs to account creation service payload
  const updatedAppState = {
    ...app,
    fundingMethodType: fundingTypes.FUND_LATER,
    priorYearAmount: 0,
    currentYearAmount: 0,
    newInvestments: cleanedInvestments,
    termsIDs: stringTermsIDs,
    deviceToken: encodeDeviceprint()
  };

  // get the service error code and tracking id on the initial submission request
  const {
    serviceErrorCode,
    trackingIdentifier
  } = await createContributoryApplicationService(updatedAppState, consumerName);

  const isSubmissionTimedOut = serviceErrorCode === 504;

  if (isSubmissionTimedOut) {
    return {
      loadedData: {
        is504Timeout: true
      },
      app: {
        applicationStatus: applicationStatusTypes.COMPLETED
      }
    };
  }

  const submissionResult = await pollForSubmissionResponse(trackingIdentifier);
  return submissionResult;
};
// specifies loading message when creating account at end
createApplication.message = 'Creating your new account';
createApplication.secondaryMessage = secondaryCommitMessage;

export const updateAnalytics: DataLoader = async (
  { store },
  flowProps
): Promise<StateStoreOrVoid> => {
  const {
    amount,
    app: { iraAccountType },
    authenticated,
    consumerName,
    loadedData: { newUserCreated },
    summitData
  } = store;

  analyticsDataLayer({
    accountType: accountTypesAnalytics.CONTRIBUTORY[iraAccountType],
    productType: productTypesAnalytics.MUTUAL_FUND,
    sourceType: getAppSourceType(consumerName),
    userType: authenticated && !newUserCreated ? 'Existing' : 'New',
    amount,
    isComplete: Boolean(flowProps?.analytics?.isComplete),
    clientTier: summitData?.assetTierID?.toString()
  });
  dispatchPageLoad(flowProps?.analytics?.pageName);
};

export const loadTermsAndContributionPeriod: DataLoader = async appState => {
  // load terms for rj scenario when user is dropped back on review
  const submissionTerms = await loadTermsForSubmission(appState);

  // load contributionPeriod data for "My Funding" section on review
  const contributionPeriod = await getContributionPeriodService(
    appState?.store?.app?.customer?.residentialAddress?.state
  );

  if (appState?.store?.prospect) {
    /* convert prospect users to RSP entity */
    await convertProspectCustomer();
  }

  return { ...submissionTerms, loadedData: { contributionPeriod } };
};
